
import { Component, Vue } from 'vue-property-decorator';
import { WPXFormValue } from '@/components/templates/WPXForm';
import { EditRepeatableEventForm } from '@/services/Calendar/RepeatableEvents/forms/EditRepeatableEventForm';
import {
  ApiUpdateRepeatableEventsInterface,
  RepeatableEventsInterface,
} from '@/services/Calendar/RepeatableEvents/Interfaces';
import RepeatableEventsRepository from '@/services/Calendar/RepeatableEvents';
import PageHeader from '@/components/layout/PageHeader.vue';

@Component({
  components: {
    PageHeader,
  },
  inject: [
    'repeatableEventsRepository',
  ],
})
export default class EditRepeatableEvent extends Vue {
  private readonly repeatableEventsRepository!: RepeatableEventsRepository;

  private loading: boolean = true;
  private dateMenu: boolean = false;
  private attendanceLimit: boolean = false;

  private recurringEvent: Partial<RepeatableEventsInterface> = {};
  private form: WPXFormValue<EditRepeatableEventForm> = new WPXFormValue(EditRepeatableEventForm);
  private repeatEvery: Array<{ value: number, text: string }> = [{
    value: 0,
    text: 'Weekly',
  }, {
    value: 1,
    text: 'Once every other week',
  }, {
    value: 2,
    text: 'Weekly Custom',
  }, {
    value: 3,
    text: 'Monthly',
  }, {
    value: 4,
    text: 'Yearly',
  }];
  private weeklyCustom: Array<{ value: number, text: string }> = [{
    value: 0,
    text: 'Sunday',
  }, {
    value: 1,
    text: 'Monday',
  }, {
    value: 2,
    text: 'Tuesday',
  }, {
    value: 3,
    text: 'Wednesday',
  }, {
    value: 4,
    text: 'Thursday',
  }, {
    value: 5,
    text: 'Friday',
  }, {
    value: 6,
    text: 'Saturday',
  }];

  private async created(): Promise<void> {
    if (!this.$route.params.eventId) {
      await this.$router.push({ name: 'calendar_manage_events' });
      this.$notifications.error('National event ID not provided.');

      return;
    }

    try {
      this.recurringEvent = await this.repeatableEventsRepository.getRepeatableEvent(Number(this.$route.params.eventId));
      this.form.data.name = String(this.recurringEvent.name);
      this.form.data.description = String(this.recurringEvent.description);
      this.form.data.location = this.recurringEvent.location ?? '';
      this.form.data.author = String(this.recurringEvent.author);
      this.form.data.withAttendance = Boolean(this.recurringEvent.withAttendance);

      if (this.recurringEvent.attendanceLimit) {
        this.form.data.attendanceLimit = this.recurringEvent.attendanceLimit;
      }

      if (this.recurringEvent.repeatEvery) {
        this.form.data.repeatEvery = this.recurringEvent.repeatEvery;
      }

      if (this.recurringEvent.date) {
        this.form.data.date = new Date(this.recurringEvent.date).toISOString().substr(0, 10);
      }

      if (this.recurringEvent.signUpEndDate && this.recurringEvent.signUpEndDate > 0) {
        this.form.data.signUpEndDate = this.recurringEvent.signUpEndDate;
      }

      if (this.recurringEvent.weeklyCustom && this.form.data.repeatEvery === 2) {
        this.form.data.weeklyCustom = this.recurringEvent.weeklyCustom;
      }
    } catch (e) {
      console.error(e);
    }

    this.loading = false;
  }

  private async edit(): Promise<void> {
    try {
      const repeatableEvent = {
        id: Number(this.$route.params.eventId),
        name: this.form.data.name,
        description: this.form.data.description,
        location: this.form.data.location,
        author: this.form.data.author,
        withAttendance: this.form.data.withAttendance,
        attendanceLimit: this.attendanceLimit ? Number(this.form.data.attendanceLimit) : undefined,
        date: this.form.data.date ? new Date(this.form.data.date) : new Date(),
        repeatEvery: this.form.data.repeatEvery,
      } as unknown as ApiUpdateRepeatableEventsInterface;

      if (this.form.data.signUpEndDate > 0) {
        repeatableEvent.signUpEndDate = Number(this.form.data.signUpEndDate);
      }

      if (this.form.data.repeatEvery === 2) {
        repeatableEvent.weeklyCustom = this.form.data.weeklyCustom;
      }

      await this.repeatableEventsRepository.updateRepeatableEvent(repeatableEvent);

      this.$successDialog.show(`Repeatable event '${this.form.data.name}' has been edited.`);
    } catch (err) {
      this.$errorDialog.show(err.response.data.error);
    }
  }
}
